import { useEffect, useMemo } from 'react'
import { styled } from '@mui/material'
import HeaderPopper from 'features/common/ui/AppTemplate/Header/Partials/HeaderPopper'
import { useAppDispatch, useAppSelector } from 'app/state'
import { Job, JobStatusType, JobType, JobsPopperTab, isImportingJob, isProcessingJob } from '../types'
import JobsTabs from './JobsTabs/JobsTabs'
import JobsTabsHeader from './JobsTabs/JobsTabsHeader'
import useProcessingJobs from './useJobs/useProcessingJobs'
import useExportingJobs from './useJobs/useExportingJobs'
import useImportingJobs from './useJobs/useImportingJobs'
import { getNewestJob } from '../models/jobs'
import JobsStatusIcon from './JobsStatusIcon'
import { setJobsPopperState } from '../state/jobsProcessingSlice'

const IconContainer = styled('div')(({ theme }) => ({
  '.MuiIconButton-root & svg path': {
    fill: theme.palette.brand.appBarContrastText
  }
}))

function getTabForJob(job: Job) {
  if (isProcessingJob(job)) {
    if (job.response?.metadata?.type === JobType.Exporting) {
      return JobsPopperTab.Exports
    } else {
      return JobsPopperTab.Processing
    }
  }
  if (isImportingJob(job)) {
    return JobsPopperTab.Imports
  }
  return JobsPopperTab.Exports
}

function JobsPopper({ lastJobStatus }: { lastJobStatus: JobStatusType | null }) {
  const dispatch = useAppDispatch()
  const open = useAppSelector((state) => state.jobs.jobsPopperOpen)
  const tab = useAppSelector((state) => state.jobs.jobsPopperTab) || JobsPopperTab.Processing

  const { processingJobsNotCleared } = useProcessingJobs()
  const { exportingJobsNotCleared } = useExportingJobs()
  const { importingJobsNotCleared } = useImportingJobs()
  const layerExportJobs = useAppSelector((state) => state.jobsExporting.exports)

  const latestJob = useMemo(() => {
    return getNewestJob([
      ...processingJobsNotCleared,
      ...exportingJobsNotCleared,
      ...importingJobsNotCleared,
      ...layerExportJobs
    ])
  }, [exportingJobsNotCleared, importingJobsNotCleared, layerExportJobs, processingJobsNotCleared])

  const handleOpen = () => {
    dispatch(setJobsPopperState({ open: true }))
  }

  const handleClose = () => {
    dispatch(setJobsPopperState({ open: false }))
  }

  const setSelectedTab = (tab: JobsPopperTab) => {
    dispatch(setJobsPopperState({ tab }))
  }

  useEffect(() => {
    if (open) return

    if (!latestJob) {
      // autoclose
      dispatch(setJobsPopperState({ open: false }))
    } else {
      // and auto select tab based on latest job
      const tab = getTabForJob(latestJob)
      dispatch(setJobsPopperState({ tab }))
    }
    // specifically ignore open, so we _only_ reexecute it when latestJob changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, latestJob])

  return (
    <HeaderPopper
      icon={
        <IconContainer>
          <JobsStatusIcon status={lastJobStatus} />
        </IconContainer>
      }
      open={open}
      handleOpen={handleOpen}
      handleClose={handleClose}
      ariaLabelButton="Jobs status panel"
    >
      <>
        <JobsTabsHeader selectedTab={tab} onTabChange={(tab) => setSelectedTab(tab)} />
        <JobsTabs selectedTab={tab} />
      </>
    </HeaderPopper>
  )
}

export default JobsPopper
